import React, { useCallback, useEffect, useState } from 'react';
import { Save, Tag, Trash2 } from 'lucide-react';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { useToast } from './ui/Toast';
import apiClient from '../api/client';

type Coupon = { _id: string; code: string; type: 'percentage' | 'fixed'; value: number; minOrderAmount: number; maxUses: number | null; usedCount: number; expiresAt: string | null; isActive: boolean };
type CouponForm = Omit<Coupon, '_id' | 'usedCount'>;
const blankCoupon: CouponForm = { code: '', type: 'percentage', value: 10, minOrderAmount: 0, maxUses: null, expiresAt: null, isActive: true };

const StoreSettingsManager = () => {
  const { showToast } = useToast();
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [savingFee, setSavingFee] = useState(false);
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [couponForm, setCouponForm] = useState<CouponForm>(blankCoupon);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [savingCoupon, setSavingCoupon] = useState(false);

  const load = useCallback(async () => {
    try {
      const [settings, couponList] = await Promise.all([apiClient.get('/settings'), apiClient.get('/coupons')]);
      setDeliveryFee(settings.data.deliveryFee);
      setCoupons(couponList.data);
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Error al cargar la configuración', 'error');
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const saveDeliveryFee = async (event: React.FormEvent) => {
    event.preventDefault();
    setSavingFee(true);
    try {
      const response = await apiClient.put('/settings', { deliveryFee });
      setDeliveryFee(response.data.deliveryFee);
      showToast('Valor del envío actualizado', 'success');
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Error al guardar el envío', 'error');
    } finally { setSavingFee(false); }
  };

  const resetForm = () => { setCouponForm(blankCoupon); setEditingId(null); };

  const saveCoupon = async (event: React.FormEvent) => {
    event.preventDefault();
    setSavingCoupon(true);
    const payload = { ...couponForm, code: couponForm.code.trim().toUpperCase(), expiresAt: couponForm.expiresAt || null };
    try {
      if (editingId) await apiClient.put(`/coupons/${editingId}`, payload);
      else await apiClient.post('/coupons', payload);
      showToast(editingId ? 'Cupón actualizado' : 'Cupón creado', 'success');
      resetForm();
      await load();
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Error al guardar el cupón', 'error');
    } finally { setSavingCoupon(false); }
  };

  const editCoupon = (coupon: Coupon) => {
    setEditingId(coupon._id);
    setCouponForm({ code: coupon.code, type: coupon.type, value: coupon.value, minOrderAmount: coupon.minOrderAmount, maxUses: coupon.maxUses, expiresAt: coupon.expiresAt ? coupon.expiresAt.slice(0, 10) : null, isActive: coupon.isActive });
  };

  const deleteCoupon = async (coupon: Coupon) => {
    if (!window.confirm(`¿Eliminar el cupón ${coupon.code}? Si ya fue usado, se desactivará para conservar el historial.`)) return;
    try {
      await apiClient.delete(`/coupons/${coupon._id}`);
      showToast(coupon.usedCount ? 'Cupón desactivado; se conservó el historial' : 'Cupón eliminado', 'success');
      if (editingId === coupon._id) resetForm();
      await load();
    } catch (error: any) { showToast(error.response?.data?.message || 'Error al eliminar el cupón', 'error'); }
  };

  const updateForm = (field: keyof CouponForm, value: any) => setCouponForm(current => ({ ...current, [field]: value }));

  return (
    <div className="space-y-6">
      <Card className="p-6 sm:p-8">
        <h2 className="text-xl font-black mb-2">Costo de envío</h2>
        <p className="text-sm text-slate-500 mb-5">Este valor se agrega a los pedidos de domicilio y se calcula nuevamente en el servidor.</p>
        <form onSubmit={saveDeliveryFee} className="flex flex-col sm:flex-row sm:items-end gap-3 max-w-xl">
          <label className="flex-1 text-sm font-bold">Valor en pesos
            <input aria-label="Valor del envío en pesos" type="number" min="0" step="1" required value={deliveryFee} onChange={event => setDeliveryFee(Number(event.target.value))} className="mt-2 w-full h-11 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3" />
          </label>
          <Button type="submit" disabled={savingFee}>{savingFee ? 'Guardando…' : 'Guardar envío'}</Button>
        </form>
      </Card>

      <Card className="p-6 sm:p-8">
        <div className="flex items-center justify-between gap-3 mb-5"><div className="flex items-center gap-3"><Tag className="text-primary" /><div><h2 className="text-xl font-black">Códigos de descuento</h2><p className="text-sm text-slate-500">Administra descuentos que los clientes pueden aplicar al pagar.</p></div></div>{editingId && <Button type="button" variant="outline" size="sm" onClick={resetForm}>Nuevo código</Button>}</div>
        <form onSubmit={saveCoupon} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4 rounded-2xl bg-slate-50 dark:bg-slate-900/60 mb-6">
          <label className="text-sm font-bold">Código
            <input required maxLength={30} pattern="[A-Za-z0-9_-]{2,30}" value={couponForm.code} onChange={event => updateForm('code', event.target.value.toUpperCase())} placeholder="BIENVENIDO" className="mt-2 w-full h-11 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3" />
          </label>
          <label className="text-sm font-bold">Tipo
            <select value={couponForm.type} onChange={event => updateForm('type', event.target.value)} className="mt-2 w-full h-11 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3"><option value="percentage">Porcentaje</option><option value="fixed">Monto fijo</option></select>
          </label>
          <label className="text-sm font-bold">Descuento {couponForm.type === 'percentage' ? '(%)' : '(pesos)'}
            <input type="number" min="1" max={couponForm.type === 'percentage' ? 100 : undefined} step="1" required value={couponForm.value} onChange={event => updateForm('value', Number(event.target.value))} className="mt-2 w-full h-11 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3" />
          </label>
          <label className="text-sm font-bold">Pedido mínimo (pesos)
            <input type="number" min="0" step="1" value={couponForm.minOrderAmount} onChange={event => updateForm('minOrderAmount', Number(event.target.value))} className="mt-2 w-full h-11 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3" />
          </label>
          <label className="text-sm font-bold">Usos máximos (opcional)
            <input type="number" min="1" step="1" value={couponForm.maxUses ?? ''} onChange={event => updateForm('maxUses', event.target.value ? Number(event.target.value) : null)} className="mt-2 w-full h-11 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3" />
          </label>
          <label className="text-sm font-bold">Vence (opcional)
            <input type="date" value={couponForm.expiresAt || ''} onChange={event => updateForm('expiresAt', event.target.value || null)} className="mt-2 w-full h-11 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3" />
          </label>
          <label className="flex items-center gap-2 text-sm font-bold"><input type="checkbox" checked={couponForm.isActive} onChange={event => updateForm('isActive', event.target.checked)} />Activo para clientes</label>
          <div className="flex gap-2 sm:col-span-2 lg:col-span-2"><Button type="submit" disabled={savingCoupon}><Save size={16} className="mr-2" />{editingId ? 'Guardar cambios' : 'Crear código'}</Button>{editingId && <Button type="button" variant="outline" onClick={resetForm}>Cancelar edición</Button>}</div>
        </form>
        <div className="space-y-3">
          {coupons.length === 0 && <p className="text-sm text-slate-500">Todavía no hay códigos de descuento.</p>}
          {coupons.map(coupon => <div key={coupon._id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border border-slate-100 dark:border-slate-800 rounded-xl p-4">
            <div><div className="font-black">{coupon.code} <span className={`ml-2 text-[10px] uppercase ${coupon.isActive ? 'text-green-600' : 'text-slate-400'}`}>{coupon.isActive ? 'Activo' : 'Inactivo'}</span></div><p className="text-xs text-slate-500">{coupon.type === 'percentage' ? `${coupon.value}%` : `$${coupon.value.toLocaleString()}`} · mínimo ${coupon.minOrderAmount.toLocaleString()} · {coupon.usedCount}{coupon.maxUses ? `/${coupon.maxUses}` : ''} usos</p></div>
            <div className="flex gap-2"><Button type="button" variant="outline" size="sm" onClick={() => editCoupon(coupon)}>Editar</Button><Button type="button" variant="outline" size="sm" aria-label={`Eliminar ${coupon.code}`} onClick={() => deleteCoupon(coupon)}><Trash2 size={15} /></Button></div>
          </div>)}
        </div>
      </Card>
    </div>
  );
};

export default StoreSettingsManager;
